import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Play as PlayIcon, Heart, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { timeAgo } from "@/components/CommunityClips";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Public "Clip of the Week" spotlight for the logged-out Landing page. Auto-picks the
// single best recent clip (Staff Pick / most-liked). Renders nothing when no clips exist.
export default function ClipOfTheWeek() {
  const navigate = useNavigate();
  const [clip, setClip] = useState(null);
  const playedRef = useRef(false);

  useEffect(() => {
    api.get("/clips/spotlight").then(({ data }) => setClip(data.clip || null)).catch(() => setClip(null));
  }, []);

  if (!clip) return null;

  const onPlay = () => {
    if (playedRef.current) return;
    playedRef.current = true;
    api.post(`/clips/${clip.id}/play`).catch(() => {});
  };

  return (
    <section className="relative z-10 max-w-6xl mx-auto px-5 py-14" data-testid="clip-of-the-week">
      <div className="relative overflow-hidden rounded-3xl border border-amber-400/25 bg-gradient-to-br from-amber-500/10 via-fuchsia-500/5 to-transparent p-5 sm:p-8">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-center">
          <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black">
            <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-amber-400 text-black shadow" data-testid="cotw-badge">
              <Trophy className="w-3.5 h-3.5 fill-current" /> CLIP OF THE WEEK
            </div>
            <video src={`${BACKEND}${clip.url}`} autoPlay muted loop playsInline controls onPlay={onPlay}
              className="w-full block bg-black" style={{ aspectRatio: "16/9" }} data-testid="cotw-video" />
          </div>

          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-500/15 text-fuchsia-300 text-xs font-bold mb-3">
              <Sparkles className="w-3.5 h-3.5" /> BUILT &amp; PLAYED WITH AI
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3 leading-tight" data-testid="cotw-caption">
              {clip.caption || "A wild gameplay moment"}
            </h2>
            <p className="text-white/70 mb-1">
              from{" "}
              <button onClick={() => navigate(`/g/${clip.game_id}`)} className="font-semibold text-amber-300 hover:underline" data-testid="cotw-game-link">
                {clip.cover_emoji || "🎮"} {clip.game_title || "this game"}
              </button>
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/50 mb-6">
              <span>by {clip.uploader_name || "a player"}</span>
              {clip.created_at && <span>· {timeAgo(clip.created_at)}</span>}
              <span className="flex items-center gap-1"><PlayIcon className="w-3.5 h-3.5" /> {clip.plays || 0}</span>
              <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> {clip.likes || 0}</span>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => navigate(`/g/${clip.game_id}`)} size="lg"
                className="rounded-full gap-2 bg-amber-400 text-black font-bold hover:bg-amber-300" data-testid="cotw-play-btn">
                <PlayIcon className="w-5 h-5 fill-current" /> Play this game
              </Button>
              <Button onClick={() => navigate("/register")} size="lg" variant="outline"
                className="rounded-full gap-2 border-white/20 text-white hover:bg-white/10" data-testid="cotw-build-btn">
                <Sparkles className="w-5 h-5" /> Build your own with AI <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
